import { ApolloClient } from '@apollo/client/core';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import _get from 'lodash/get';

import {
  GetBusinessInfoQueryResult, getRFXBusinessInfoQuery
} from '../../gql/queries/getRFXBusinessInfoQuery';
import { KeyValueMap, StringMap } from '../../interfaces';
import { DEFAULT_QUOTE_VALIDITY, QuoteValidityTable } from '../../interfaces/RFXBusinessModel';
import clusterToRegionMapping from '../../resources/clusterToRegionMapping.json';
import { RootState } from '../../store';

const name = 'businessInfo';

export interface BusinessInfoProps {
  isLoading: boolean;
  errorMessage: string;
  regionToAreaMap: StringMap | null;
  businessToQuoteValidityTable: KeyValueMap<QuoteValidityTable>;
}
export interface FetchQuoteValidityArgs {
  client: ApolloClient<object>;
  businessId: string;
}

export interface FetchQuoteValidityResult {
  businessId: string;
  queryResult: GetBusinessInfoQueryResult;
}

const initialState: BusinessInfoProps = {
  isLoading: false,
  errorMessage: '',
  regionToAreaMap: null,
  businessToQuoteValidityTable: {}
}

export const fetchBusinessInfo = createAsyncThunk<
  FetchQuoteValidityResult | null,
  FetchQuoteValidityArgs,
  {
    state: RootState,
    rejectValue: string
  }>(
    `${name}/fetchBusinessInfo`,
    async (args: FetchQuoteValidityArgs, { getState }) => {
      const { client, businessId } = args;
      const state: RootState = getState();

      if (businessId in state[name].businessToQuoteValidityTable) {
        console.log('no need to fetch.')
        return null;
      }
      const response = await client.query({
        query: getRFXBusinessInfoQuery,
        variables: { businessId }
      })
      console.log('fetchBusinessInfo done!');
      const queryResult = response.data as GetBusinessInfoQueryResult;
      return {
        businessId,
        queryResult
      };
    }
  );

const businessInfoSlice = createSlice({
  name,
  initialState,
  reducers: {
    setRegionToAreaMap(state, action: PayloadAction<StringMap>) {
      state.regionToAreaMap = action.payload;
    },
    setBusinessToQuoteValidityTable(state, action: PayloadAction<KeyValueMap<QuoteValidityTable>>) {
      state.businessToQuoteValidityTable = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBusinessInfo.fulfilled, (state, action) => {
      if (action.payload !== null) {
        const { businessId, queryResult } = action.payload;
        const quoteValidityInfo = queryResult.getRFXBusinessInfo.quoteValidityInfo;
        if (state.regionToAreaMap === null) {
          state.regionToAreaMap = JSON.parse(quoteValidityInfo.stringifiedRegionToAreaMap);
        }
        if (!(businessId in state.businessToQuoteValidityTable)) {
          state.businessToQuoteValidityTable[businessId] = JSON.parse(quoteValidityInfo.stringifiedQuoteValidityTable);
        }
      }
      state.isLoading = false;
    });
    builder.addCase(fetchBusinessInfo.rejected, (state, action) => {
      state.errorMessage = action.payload || '';
      state.isLoading = false;
    });
    builder.addCase(fetchBusinessInfo.pending, (state) => {
      state.isLoading = true;
    });
  }
});

// TODO: used in QuoteValidity.test.tsx. can be removed if we know how to mock Asyncthunk
export const {
  setRegionToAreaMap,
  setBusinessToQuoteValidityTable
} = businessInfoSlice.actions;

export const selectHasBusinessInfo = (businessId: string | null) =>
  (state: RootState): boolean => {

    if (businessId === null) {
      return false;
    }
    const businessToQuoteValidityTable = state[name].businessToQuoteValidityTable;
    return businessId in businessToQuoteValidityTable;
  }

export const selectQuoteValidity = (
  businessId: string | null,
  region: string | undefined,
  category: string | undefined
) => (state: RootState): number => {
  if (businessId === null || region === undefined || category === undefined) {
    return DEFAULT_QUOTE_VALIDITY;
  }
  const businessToQuoteValidityTable = state[name].businessToQuoteValidityTable;
  if (!(businessId in businessToQuoteValidityTable)) {
    return DEFAULT_QUOTE_VALIDITY;
  }
  const quoteValidityTable = businessToQuoteValidityTable[businessId];

  const regionToAreaMap = state[name].regionToAreaMap;
  // Check whether region is a cluster
  let realRegion = _get(clusterToRegionMapping, region);
  if (realRegion === undefined) {
    realRegion = region;
  }

  const area = _get(regionToAreaMap, realRegion);
  if (area === undefined) {
    return DEFAULT_QUOTE_VALIDITY;
  }
  return _get(quoteValidityTable, [area, category], DEFAULT_QUOTE_VALIDITY);
}

export default businessInfoSlice.reducer;
